import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import Modal from './Modal';
import useFocusTrap from './filters/useFocusTrap';
import { Button } from '../ui/Button';
import { Field } from '../ui/Field';
import { TextInput } from '../ui/Input';
import { cn } from '../../utils/cn';

const TONES = {
    body: 'text-fg-secondary',
    impact: 'text-fg-secondary',
    strong: 'text-fg',
};

// tone -> Button variant for the confirm action.
const CONFIRM_VARIANT = { danger: 'destructive', default: 'default' };

/**
 * Presentational confirmation dialog. Most code should call `useConfirm()` instead of
 * rendering this directly.
 *
 * Destructive dialogs focus Cancel first; dialogs with `typeToConfirm` focus the text box
 * and keep the confirm button disabled until the exact text is typed. Escape cancels without
 * closing any dialog underneath.
 */
export const ConfirmDialog = ({
    open,
    title,
    body,
    impact,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    tone = 'default',
    typeToConfirm,
    onConfirm,
    onCancel,
}) => {
    const panelRef = useRef(null);
    const cancelRef = useRef(null);
    const confirmRef = useRef(null);
    const inputRef = useRef(null);
    const bodyId = useId();
    const [typed, setTyped] = useState('');

    useFocusTrap(open, panelRef, onCancel);

    useEffect(() => {
        if (!open) return;
        setTyped('');
        const target = typeToConfirm ? inputRef : tone === 'danger' ? cancelRef : confirmRef;
        target.current?.focus();
    }, [open, tone, typeToConfirm]);

    const canConfirm = !typeToConfirm || typed.trim() === typeToConfirm;

    const submit = (e) => {
        e.preventDefault();
        if (canConfirm) onConfirm();
    };

    return (
        <Modal
            isOpen={open}
            onClose={onCancel}
            title={title}
            size="sm"
            role="alertdialog"
            describedBy={body || impact?.length ? bodyId : undefined}
            panelRef={panelRef}
        >
            <form onSubmit={submit} className="space-y-4">
                {(body || impact?.length > 0) && (
                    <div id={bodyId} className="space-y-3">
                        {body && <p className={cn('text-sm', TONES.body)}>{body}</p>}
                        {impact?.length > 0 && (
                            <ul className={cn('ml-2 list-inside list-disc space-y-1 text-sm', TONES.impact)}>
                                {impact.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {typeToConfirm && (
                    <Field
                        label={
                            <>
                                Type <strong className={TONES.strong}>{typeToConfirm}</strong> to confirm
                            </>
                        }
                    >
                        <TextInput
                            ref={inputRef}
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                        />
                    </Field>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <Button ref={cancelRef} type="button" variant="outline" onClick={onCancel}>
                        {cancelLabel}
                    </Button>
                    <Button
                        ref={confirmRef}
                        type="submit"
                        variant={CONFIRM_VARIANT[tone] || CONFIRM_VARIANT.default}
                        disabled={!canConfirm}
                    >
                        {confirmLabel}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

const ConfirmContext = createContext(null);

/**
 * Mount once above every page that confirms actions (ShellLayout). Renders the single
 * shared ConfirmDialog and exposes the promise-based `confirm()` through `useConfirm()`.
 */
export const ConfirmProvider = ({ children }) => {
    const [request, setRequest] = useState(null);
    const requestRef = useRef(null);

    const settle = useCallback((value) => {
        requestRef.current?.resolve(value);
        requestRef.current = null;
        setRequest(null);
    }, []);

    const confirm = useCallback((options) => {
        // A new request supersedes an unanswered one, which counts as cancelled.
        requestRef.current?.resolve(false);
        return new Promise((resolve) => {
            requestRef.current = { options, resolve };
            setRequest(requestRef.current);
        });
    }, []);

    const onConfirm = useCallback(() => settle(true), [settle]);
    const onCancel = useCallback(() => settle(false), [settle]);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <ConfirmDialog open={Boolean(request)} {...request?.options} onConfirm={onConfirm} onCancel={onCancel} />
        </ConfirmContext.Provider>
    );
};

/**
 * Ask the user to confirm an action. Resolves true on confirm, false on cancel or Escape.
 *
 * @example
 * const confirm = useConfirm();
 * if (!(await confirm({ title: `Delete role ${name}?`, body: '…', confirmLabel: 'Delete', tone: 'danger' }))) return;
 *
 * @returns {(options: {
 *   title: string,
 *   body?: React.ReactNode,
 *   impact?: string[],
 *   confirmLabel?: string,
 *   cancelLabel?: string,
 *   tone?: 'default'|'danger',
 *   typeToConfirm?: string,
 * }) => Promise<boolean>}
 */
export const useConfirm = () => {
    const confirm = useContext(ConfirmContext);
    if (!confirm) throw new Error('useConfirm must be used inside <ConfirmProvider>.');
    return confirm;
};
